import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SwipeGestureEvent, SwipeThresholds, SwipeAnimationConfig } from '@/types/swipe';
import { SWIPE_THRESHOLDS, ANIMATION_CONFIG } from '@/lib/SwipeInterface/constants';

interface SwipeSliceState {
  currentIndex: number;
  swipeHistory: SwipeGestureEvent[];
  thresholds: SwipeThresholds;
  animationConfig: SwipeAnimationConfig;
  isSwipeEnabled: boolean;
  lastSwipeDirection: 'left' | 'right' | null;
  swipeCount: {
    left: number;
    right: number;
  };
}

const initialState: SwipeSliceState = {
  currentIndex: 0,
  swipeHistory: [],
  thresholds: {
    distance: SWIPE_THRESHOLDS.DISTANCE,
    velocity: SWIPE_THRESHOLDS.VELOCITY,
    snapBack: SWIPE_THRESHOLDS.SNAP_BACK,
  },
  animationConfig: {
    duration: ANIMATION_CONFIG.DURATION,
    easing: ANIMATION_CONFIG.EASING,
    exitVelocity: ANIMATION_CONFIG.EXIT_VELOCITY,
    snapBackThreshold: 80,
    rotationFactor: ANIMATION_CONFIG.ROTATION_FACTOR,
  },
  isSwipeEnabled: true,
  lastSwipeDirection: null,
  swipeCount: {
    left: 0,
    right: 0,
  },
};

const swipeSlice = createSlice({
  name: 'swipe',
  initialState,
  reducers: {
    recordSwipe: (state, action: PayloadAction<SwipeGestureEvent>) => {
      const swipeEvent = action.payload;
      
      // Add to history
      state.swipeHistory.unshift(swipeEvent);
      
      // Keep only last 50 swipes
      if (state.swipeHistory.length > 50) {
        state.swipeHistory = state.swipeHistory.slice(0, 50);
      }
      
      // Update counters
      state.swipeCount[swipeEvent.direction]++;
      state.lastSwipeDirection = swipeEvent.direction;
      
      // Advance index
      state.currentIndex++;
    },

    resetSwipeState: (state) => {
      state.currentIndex = 0;
      state.swipeHistory = [];
      state.lastSwipeDirection = null;
      state.swipeCount = { left: 0, right: 0 };
    },

    setCurrentIndex: (state, action: PayloadAction<number>) => {
      state.currentIndex = Math.max(0, action.payload);
    },

    updateThresholds: (state, action: PayloadAction<Partial<SwipeThresholds>>) => {
      state.thresholds = { ...state.thresholds, ...action.payload };
    },

    updateAnimationConfig: (state, action: PayloadAction<Partial<SwipeAnimationConfig>>) => {
      state.animationConfig = { ...state.animationConfig, ...action.payload };
    },

    setSwipeEnabled: (state, action: PayloadAction<boolean>) => {
      state.isSwipeEnabled = action.payload;
    },

    clearSwipeHistory: (state) => {
      state.swipeHistory = [];
    },

    undoLastSwipe: (state) => {
      if (state.swipeHistory.length > 0) {
        const lastSwipe = state.swipeHistory.shift()!;
        state.swipeCount[lastSwipe.direction]--;
        state.currentIndex = Math.max(0, state.currentIndex - 1);
        
        // Update last direction
        state.lastSwipeDirection = state.swipeHistory.length > 0 
          ? state.swipeHistory[0].direction 
          : null;
      }
    },

    // Analytics and insights
    getSwipeVelocityStats: (state) => {
      if (state.swipeHistory.length === 0) return null;
      
      const velocities = state.swipeHistory.map(s => s.velocity);
      const avgVelocity = velocities.reduce((sum, v) => sum + v, 0) / velocities.length;
      const maxVelocity = Math.max(...velocities);
      const minVelocity = Math.min(...velocities);
      
      return { avgVelocity, maxVelocity, minVelocity };
    },

    getSwipeStats: (state) => {
      const total = state.swipeCount.left + state.swipeCount.right;
      const leftPercentage = total > 0 ? (state.swipeCount.left / total) * 100 : 0;
      const rightPercentage = total > 0 ? (state.swipeCount.right / total) * 100 : 0;
      
      return {
        total,
        leftCount: state.swipeCount.left,
        rightCount: state.swipeCount.right,
        leftPercentage,
        rightPercentage,
      };
    },
  },
});

export const {
  recordSwipe,
  resetSwipeState,
  setCurrentIndex,
  updateThresholds,
  updateAnimationConfig,
  setSwipeEnabled,
  clearSwipeHistory,
  undoLastSwipe,
  getSwipeVelocityStats,
  getSwipeStats,
} = swipeSlice.actions;

export default swipeSlice.reducer;